import React from 'react';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TableCell from 'src/components/Table/TableCell';
import { PaymentMethod } from 'src/utils/constants/cfdi';
import dateFormatType from 'src/utils/dateFormatType';
import { formatPositiveAmount } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import TooltipTypography from 'src/components/TooltipTypography/TooltipTypography';
import StatusBadge from 'src/components/StatusBadge';

const DUE_DAYS_CURRENT_COLOR = '#7cb342';
const DUE_EMPTY_COLOR = '#9aa3ad';
const PENDING_COLOR = '#854F0B';
const PAID_COLOR = '#7cb342';

const PAYMENT_STATUS_TONE = {
  PAID: 'success',
  PARTIAL: 'info',
  PENDING: 'warning'
};

const OverdueCell = ({ paymentMethod, pendingAmount, daysOverdue }) => {
  if (paymentMethod !== PaymentMethod.PPD || !pendingAmount) {
    return (
      <Typography sx={{ color: DUE_EMPTY_COLOR, fontWeight: 600 }}>
        —
      </Typography>
    );
  }
  if (daysOverdue < 0) {
    return (
      <Typography sx={{ color: DUE_DAYS_CURRENT_COLOR, fontWeight: 800 }}>
        {daysOverdue}
      </Typography>
    );
  }
  if (!daysOverdue) {
    return (
      <Typography sx={{ color: DUE_EMPTY_COLOR, fontWeight: 600 }}>
        —
      </Typography>
    );
  }
  return (
    <Typography
      sx={(theme) => ({
        fontWeight: 800,
        color:
          daysOverdue > 60
            ? theme.palette.badge.error.text
            : theme.palette.badge.warning.text
      })}
    >
      {daysOverdue}
    </Typography>
  );
};

OverdueCell.propTypes = {
  paymentMethod: PropTypes.string,
  pendingAmount: PropTypes.number,
  daysOverdue: PropTypes.number
};

OverdueCell.defaultProps = {
  paymentMethod: null,
  pendingAmount: null,
  daysOverdue: null
};

const GlobalInvoiceRow = ({
  invoiceDbId,
  amountIvaIncluded,
  date,
  client,
  clientId,
  clientRfc,
  contractId,
  contractFolioWithoutPrefix,
  category,
  folio,
  paymentMethod,
  pendingAmount,
  paymentStatus,
  complementCount,
  daysOverdue,
  onView
}) => {
  const { t } = useTranslation();

  const complementLabel =
    complementCount === 1
      ? `1 ${t('TRANSACTION.COMPLEMENT_SINGULAR')}`
      : `${complementCount} ${t('TRANSACTION.COMPLEMENT_PLURAL')}`;

  return (
    <TableRow hover>
      <TableCell align="center">
        <Typography>{selectFormatDate(date, dateFormatType.DAY)}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography>
          {selectFormatDate(date, dateFormatType.MONTH)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {folio ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Typography
              component={Link}
              to={`/invoice/${invoiceDbId}`}
              sx={(theme) => ({
                color: theme.palette.secondary.edit,
                fontWeight: 800,
                textDecoration: 'none'
              })}
            >
              {folio}
            </Typography>
            {paymentMethod && (
              <StatusBadge
                tone={paymentMethod === PaymentMethod.PPD ? 'info' : 'neutral'}
              >
                {paymentMethod}
              </StatusBadge>
            )}
          </Box>
        ) : (
          <TooltipTypography
            title={t('TRANSACTION.NO_INVOICE_ASSOCIATED')}
            text="--"
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Link
            to={`/clients/detail/${clientId}/accounting`}
            style={{ textDecoration: 'none' }}
          >
            <Typography
              sx={(theme) => ({
                color: theme.palette.secondary.edit,
                fontWeight: 700
              })}
            >
              {client}
            </Typography>
          </Link>
          <Typography variant="caption" color="text.secondary">
            {clientRfc}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        {contractId ? (
          <Link to={`/contracts/${contractId}`}>
            <Typography
              sx={(theme) => ({
                color: theme.palette.secondary.edit,
                fontWeight: 800
              })}
            >
              {contractFolioWithoutPrefix}
            </Typography>
          </Link>
        ) : (
          <TooltipTypography
            title={t('TRANSACTION.NO_CONTRACT_ASSOCIATED')}
            text="--"
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Typography>{t(`TRANSACTION.CATEGORY.${category}`)}</Typography>
      </TableCell>
      <TableCell align="center">
        {paymentStatus ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <StatusBadge tone={PAYMENT_STATUS_TONE[paymentStatus]}>
              {t(`TRANSACTION.PAYMENT_STATUS.${paymentStatus}`)}
            </StatusBadge>
            <Typography variant="caption" color="text.secondary">
              {complementLabel}
            </Typography>
          </Box>
        ) : (
          <Typography color="text.disabled">—</Typography>
        )}
      </TableCell>
      <TableCell align="center">
        <OverdueCell
          paymentMethod={paymentMethod}
          pendingAmount={pendingAmount}
          daysOverdue={daysOverdue}
        />
      </TableCell>
      <TableCell align="center">
        <Tooltip title={formatPositiveAmount(amountIvaIncluded)}>
          {pendingAmount === 0 ? (
            <Typography sx={{ color: PAID_COLOR, fontWeight: 800 }}>
              {t('TRANSACTION.LIQUIDATED')}
            </Typography>
          ) : pendingAmount ? (
            <Typography sx={{ color: PENDING_COLOR, fontWeight: 800 }}>
              {formatPositiveAmount(pendingAmount)}
            </Typography>
          ) : (
            <Typography sx={{ color: DUE_EMPTY_COLOR, fontWeight: 600 }}>
              —
            </Typography>
          )}
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <IconButton
          sx={{ width: 32, height: 32, color: 'primary.main' }}
          onClick={() => onView(invoiceDbId)}
          disabled={!invoiceDbId}
          aria-label={t('TRANSACTION.DETAIL.VIEW_DETAIL')}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

GlobalInvoiceRow.propTypes = {
  invoiceDbId: PropTypes.number,
  amountIvaIncluded: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  client: PropTypes.string,
  clientId: PropTypes.number,
  clientRfc: PropTypes.string,
  contractId: PropTypes.string,
  contractFolioWithoutPrefix: PropTypes.string,
  category: PropTypes.string,
  folio: PropTypes.string,
  paymentMethod: PropTypes.string,
  pendingAmount: PropTypes.number,
  paymentStatus: PropTypes.string,
  complementCount: PropTypes.number,
  daysOverdue: PropTypes.number,
  onView: PropTypes.func.isRequired
};

GlobalInvoiceRow.defaultProps = {
  invoiceDbId: null,
  client: null,
  clientId: null,
  clientRfc: null,
  contractId: null,
  contractFolioWithoutPrefix: null,
  category: null,
  folio: null,
  paymentMethod: null,
  pendingAmount: null,
  paymentStatus: null,
  complementCount: 0,
  daysOverdue: null
};

export default GlobalInvoiceRow;
